import React from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  SimpleGrid,
  Icon,
} from '@chakra-ui/react';
import { FaChevronRight } from 'react-icons/fa';
import {
  FaBone,
  FaHeartbeat,
  FaSyringe,
  FaXRay
} from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import { RevealGroup, RevealItem } from '../components/motion';
import { PageHero, SectionHeading, IconMark, InkBand, LightSheet } from '../components/premium';
import { clearGlass } from '../components/glass';

// One easing curve for every hover on the page, the same one the motion kit
// uses for arrivals, so hovers settle rather than snap.
const SETTLE = 'cubic-bezier(0.16,1,0.3,1)';

const Specialties = () => {
  const primarySpecialties = [
    {
      name: 'ORTHOPEDICS',
      icon: FaBone,
      color: '#2196f3',
      url: '/specialties/orthopedics',
      cptCodes: [
        '27447: Total knee arthroplasty',
        '20610: Drain/inj joint/bursa w/o US',
        '20550: Inj tendon sheath/ligament',
        '27130: Total hip arthroplasty',
        '20611: Drain/inj joint/bursa w/ US'
      ]
    },
    {
      name: 'INTERVENTIONAL CARDIOLOGY',
      icon: FaHeartbeat,
      color: '#00bcd4',
      url: '/specialties/cardiovascular',
      cptCodes: [
        '93005: Electrocardiogram tracing',
        '93010: Electrocardiogram report',
        '36591: Draw blood off venous device',
        '93018: Cardiovascular stress test',
        '93280: Pacemaker device programming eval (dual)'
      ]
    },
    {
      name: 'PAIN MANAGEMENT',
      icon: FaSyringe,
      color: '#2196f3',
      url: '/specialties/pain-management',
      cptCodes: [
        '64493: Inj paravert f jnt l/s 1 lev',
        '64494: Inj paravert f jnt l/s 2 lev',
        '64483: Njx aa&/strd tfrm epi l/s 1',
        '64447: Njx aa&/strd femoral nrv img',
        '64488: Tap block bilateral injection'
      ]
    },
    {
      name: 'SPINE',
      icon: FaXRay,
      color: '#00bcd4',
      url: '/specialties/spine',
      cptCodes: [
        '62323: Njx interlaminar lumbar/sacral',
        '62321: Njx interlaminar cervical/thoracic',
        '22853: Insert biomechanical device',
        '63047: Laminectomy, facetectomy & foraminotomy (lumbar)',
        '63685: Insert/redo spinal neurostimulator generator'
      ]
    }
  ];

  const otherSpecialties = [
    ['Bariatrics', 'Colonoscopy + Colorectal Resection', 'ENT', 'G.I.', 'General Surgery'],
    ['GYN', 'Interventional Radiology', 'Neurosurgery', 'Ophthalmology', 'Plastics'],
    ['Podiatry', 'Pulmonology', 'Urology', 'Vascular', 'Vein']
  ];

  const SpecialtyCard = ({ specialty }) => (
    <Box
      as={RouterLink}
      to={specialty.url}
      // data-group, not role="group": the card is a link and must keep
      // announcing itself as one.
      data-group
      display="flex"
      flexDirection="column"
      bg="white"
      borderRadius="24px"
      boxShadow="card"
      border="1px solid"
      borderColor="rgba(14,47,58,0.06)"
      transition={`transform 0.5s ${SETTLE}, box-shadow 0.5s ${SETTLE}`}
      _hover={{
        transform: 'translateY(-3px)',
        textDecoration: 'none',
      }}
      _focusVisible={{
        outline: '2px solid',
        outlineColor: 'brand.500',
        outlineOffset: '4px',
      }}
      height="100%"
      cursor="pointer"
      position="relative"
    >
      <HStack
        spacing={4}
        justify="space-between"
        px={{ base: 6, md: 8 }}
        pt={{ base: 6, md: 8 }}
        pb={6}
        borderBottom="1px solid"
        borderColor="rgba(14,47,58,0.06)"
      >
        <HStack spacing={4}>
          <IconMark icon={specialty.icon} />
          <Heading
            size="md"
            color="ink.900"
            fontWeight="600"
            letterSpacing="-0.01em"
            lineHeight="1.3"
          >
            {specialty.name}
          </Heading>
        </HStack>
        <Icon
          as={FaChevronRight}
          color="ink.400"
          boxSize={3.5}
          flexShrink={0}
          transition={`transform 0.5s ${SETTLE}, color 0.3s`}
          _groupHover={{ transform: 'translateX(3px)', color: 'brand.700' }}
        />
      </HStack>

      <VStack align="stretch" px={{ base: 6, md: 8 }} pt={6} pb={{ base: 6, md: 8 }} spacing={4} flex="1">
        <Text
          fontSize="xs"
          fontWeight="600"
          color="ink.500"
          letterSpacing="0.14em"
          textTransform="uppercase"
        >
          Frequently Utilized CPT Codes
        </Text>

        {/* Quiet reference chips. The codes are data a coordinator reads, so
            they sit in a tabular, mono-leaning face on a neutral surface
            rather than on colored bars competing with the heading. */}
        <VStack align="flex-start" spacing={2}>
          {specialty.cptCodes.map((code, index) => (
            <Box
              key={index}
              display="inline-block"
              maxW="100%"
              px={3.5}
              py={1.5}
              bg="mist.100"
              borderRadius="pill"
            >
              <Text
                fontSize="13px"
                lineHeight="1.45"
                color="ink.700"
                fontWeight="500"
                fontFamily="'SFMono-Regular', ui-monospace, Menlo, Consolas, monospace"
                letterSpacing="-0.01em"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {code}
              </Text>
            </Box>
          ))}
        </VStack>

        {/* Click to Learn More */}
        <Text
          fontSize="sm"
          color="brand.700"
          fontWeight="600"
          mt="auto"
          pt={2}
        >
          Click to learn more →
        </Text>
      </VStack>
    </Box>
  );

  return (
    <Box>
      {/* Hero Section */}
      {/* Photographic hero, shared with every inner page. See
          components/premium/PageHero for why the photo is no longer
          hidden under a cyan gradient. */}
      <PageHero
        title={"Our Specialties"}
        subtitle={"Comprehensive surgical services delivered by expert physicians using state-of-the-art technology"}
        image="/facility/operating-room.jpg"
        imagePosition="center"
      />

      {/* Primary Specialties Section */}
      <Container maxW="container.xl" py={{ base: 20, md: 28 }}>
        <SectionHeading title="PRIMARY SPECIALTIES OFFERED" />

        {/* Cards arrive in sequence so the eye is given a reading order
            instead of a wall of equal-weight tiles. */}
        <RevealGroup>
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
            gap={{ base: 6, md: 8 }}
          >
            {primarySpecialties.map((specialty, index) => (
              <GridItem key={index}>
                <RevealItem h="100%">
                  <SpecialtyCard specialty={specialty} />
                </RevealItem>
              </GridItem>
            ))}
          </Grid>
        </RevealGroup>
      </Container>

      {/* Other Specialties Section */}
      <LightSheet tone="mist">
        <Container maxW="container.xl">
          <SectionHeading title="OTHER SPECIALTIES OFFERED" />

          {/* One grid, read in the same order as the rows above it, so the
              tiles cascade in as a single set. */}
          <RevealGroup>
            <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={{ base: 3, md: 4 }}>
              {otherSpecialties.flat().map((specialty, index) => (
                <RevealItem key={index}>
                  <Flex
                    h="100%"
                    minH="84px"
                    align="center"
                    justify="center"
                    bg="white"
                    px={4}
                    py={5}
                    textAlign="center"
                    borderRadius="18px"
                    border="1px solid"
                    borderColor="rgba(14,47,58,0.06)"
                    boxShadow="cardSoft"
                  >
                    <Text
                      fontSize="md"
                      fontWeight="500"
                      color="ink.800"
                      lineHeight="1.35"
                    >
                      {specialty}
                    </Text>
                  </Flex>
                </RevealItem>
              ))}
            </SimpleGrid>
          </RevealGroup>
        </Container>
      </LightSheet>

      {/* CTA Section */}
      <InkBand image="/facility/exterior.jpg">
        <Container maxW="container.xl">
          <VStack spacing={0} textAlign="center">
            <SectionHeading
              tone="dark"
              title="Need More Information?"
              lede="Our team is here to answer questions about procedures, scheduling, and insurance coverage"
              mb={10}
            />
            <HStack spacing={4} flexWrap="wrap" justify="center" rowGap={3}>
              <Button
                as="a"
                href="/contact"
                size="lg"
                px={8}
                variant="brandOnDark"
              >
                Contact Us
              </Button>
              <Button
                as="a"
                href="tel:+13213213935"
                size="lg"
                px={8}
                color="white"
                fontWeight="600"
                sx={clearGlass}
                transition={`transform 0.35s ${SETTLE}, background-color 0.2s`}
                _hover={{ bg: 'rgba(255,255,255,0.16)', transform: 'translateY(-2px)' }}
                _active={{ bg: 'rgba(255,255,255,0.2)' }}
              >
                Call (321) 321-3935
              </Button>
            </HStack>
          </VStack>
        </Container>
      </InkBand>
    </Box>
  );
};

export default Specialties;
